import React, { useState, useEffect, useRef } from 'react';
import { useListContext } from 'react-admin';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  PersonAdd as PersonAddIcon,
  Archive as ArchiveIcon,
  Groups as GroupsIcon,
  Schedule as ScheduleIcon,
  BarChart as BarChartIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { fetchLeadsAnalytics } from '../leads.dataProvider';
import logger from '../../utils/logger';
import QuickDateFiltersPanel from './QuickDateFiltersPanel.component';

const LeadsAnalyticsPanel = () => {
  const { filterValues } = useListContext();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [detailsTab, setDetailsTab] = useState<'status' | 'journey' | 'orgs'>('status');
  const [exporting, setExporting] = useState(false);

  const requestSeqRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Invalidate any in-flight request immediately on filter change to avoid stale overwrites.
    const requestId = ++requestSeqRef.current;
    abortControllerRef.current?.abort();
    setError(null);

    // Debounce to avoid excessive API calls.
    const timer = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setLoading(true);

      try {
        const data = await fetchLeadsAnalytics(filterValues, { signal: controller.signal });

        // Ignore obsolete responses (e.g., slower previous request finishing after a newer one)
        if (requestSeqRef.current !== requestId) return;

        setAnalytics(data);
        setUpdatedAt(new Date());
      } catch (err) {
        if (controller.signal.aborted) return;
        if (requestSeqRef.current !== requestId) return;

        const errorMessage = err instanceof Error ? err.message : String(err);
        logger.error('Failed to fetch analytics', { errorMessage });
        setError('Failed to load analytics');
      } finally {
        if (requestSeqRef.current === requestId) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      abortControllerRef.current?.abort();
    };
  }, [filterValues]);

  if (error) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading && !analytics) {
    return (
      <Box sx={{ mb: 2 }}>
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={120} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!analytics) {
    return null;
  }

  const { datasetSummary, cohortSummary, statusDistribution, funnel, organizationBreakdown, timing } = analytics;
  const formatInt = (value: number | null | undefined) => (value ?? 0).toLocaleString();
  const formatPct = (value: number | null | undefined) => `${(((value ?? 0) as number) * 100).toFixed(1)}%`;
  const formatAvgDays = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—';
    if (value > 0 && value < 1) return '<1 day';
    if (value < 10) return `${value.toFixed(1)} days`;
    return `${value.toFixed(0)} days`;
  };
  const formatAvgDaysExact = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'No data';
    return `${value.toFixed(2)} days`;
  };

  const statusLabel = (status: string) =>
    status === 'signed_up'
      ? 'Signed up'
      : status === 'uploaded'
      ? 'Uploaded'
      : status === 'active'
      ? 'Uploaded (active status)'
      : status === 'enrolled'
      ? 'Enrolled'
      : status === 'reactivated'
      ? 'Reactivated'
      : status.replace(/_/g, ' ');

  const statusTone = (status: string) =>
    status === 'enrolled'
      ? { borderColor: 'success.main', bgcolor: 'success.50', color: 'success.main' }
      : status === 'signed_up'
      ? { borderColor: 'info.main', bgcolor: 'info.50', color: 'info.main' }
      : { borderColor: 'grey.300', bgcolor: 'grey.50', color: 'text.primary' };

  // Calculate percentages for status distribution
  const datasetTotal = datasetSummary?.totalLeads ?? 0;
  const cohortTotal = cohortSummary.totalLeads;
  const otherEnrollmentLeads = Math.max(0, cohortTotal - (cohortSummary.enrolledCount ?? 0));
  const signupTimingSampleSize = timing?.signedUpAtCount ?? cohortSummary.signedUpCount ?? 0;
  const enrollmentTimingSampleSize = timing?.enrolledAtCount ?? cohortSummary.enrolledCount ?? 0;
  const otherSignupTimingLeads = Math.max(0, cohortTotal - signupTimingSampleSize);
  const otherEnrollmentTimingLeads = Math.max(0, cohortTotal - enrollmentTimingSampleSize);
  const statusPercentages = statusDistribution.map((s) => ({
    ...s,
    percentage: cohortTotal > 0 ? (s.total / cohortTotal) * 100 : 0,
  }));

  // CSV Export Functions
  const escapeCSVValue = (value: string | number): string => {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    // Always escape values that contain commas, quotes, or newlines
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // Format number for CSV export without thousand separators to avoid CSV parsing issues
  const formatIntForCSV = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '0';
    return String(value);
  };

  const getFilesystemSafeTimestamp = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  };

  const downloadCSV = (csv: string, filename: string): void => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleExport = () => {
    if (!analytics) return;

    setExporting(true);
    try {
      const csvSections: string[] = [];

      // Section 1: Summary Statistics
      csvSections.push('SUMMARY STATISTICS');
      csvSections.push('Metric,Value');
      csvSections.push(`Total Leads (Dataset),${formatIntForCSV(datasetTotal)}`);
      csvSections.push(`Total Leads (Cohort),${formatIntForCSV(cohortTotal)}`);
      csvSections.push(`Enrolled Count,${formatIntForCSV(cohortSummary.enrolledCount)}`);
      csvSections.push(`Enrolled Percentage,${formatPct(cohortTotal > 0 ? cohortSummary.enrolledCount / cohortTotal : 0)}`);
      csvSections.push(`Enrollment Rate,${(cohortSummary.enrollmentRate * 100).toFixed(1)}%`);
      csvSections.push(`Signed Up Count,${formatIntForCSV(cohortSummary.signedUpCount)}`);
      csvSections.push(`Signed Up Percentage,${formatPct(cohortTotal > 0 ? cohortSummary.signedUpCount / cohortTotal : 0)}`);
      csvSections.push(`Archived Count,${formatIntForCSV(cohortSummary.archivedCount ?? 0)}`);
      csvSections.push(`Archived Percentage,${formatPct(cohortTotal > 0 ? (cohortSummary.archivedCount ?? 0) / cohortTotal : 0)}`);
      csvSections.push('');

      // Section 2: Timing Metrics
      csvSections.push('TIMING METRICS');
      csvSections.push('Metric,Value,Sample Size');
      if (timing.avgDaysToSignup !== null && timing.avgDaysToSignup !== undefined) {
        csvSections.push(`Avg Days to Signup,${formatAvgDaysExact(timing.avgDaysToSignup)},${formatIntForCSV(signupTimingSampleSize)}`);
      } else {
        csvSections.push('Avg Days to Signup,No data,0');
      }
      if (timing.avgDaysToEnrollment !== null && timing.avgDaysToEnrollment !== undefined) {
        csvSections.push(`Avg Days to Enrollment,${formatAvgDaysExact(timing.avgDaysToEnrollment)},${formatIntForCSV(enrollmentTimingSampleSize)}`);
      } else {
        csvSections.push('Avg Days to Enrollment,No data,0');
      }
      csvSections.push('');

      // Section 3: Status Distribution
      csvSections.push('STATUS DISTRIBUTION');
      csvSections.push('Status,Total,Unarchived,Archived,Percentage');
      statusPercentages.forEach((status) => {
        csvSections.push(
          `${escapeCSVValue(statusLabel(status.status))},${formatIntForCSV(status.total)},${formatIntForCSV(status.unarchived)},${formatIntForCSV(status.archived)},${status.percentage.toFixed(1)}%`
        );
      });
      csvSections.push('');

      // Section 4: Journey Funnel
      csvSections.push('JOURNEY FUNNEL');
      csvSections.push('Stage,Count,Percentage');
      funnel.stages.forEach((stage) => {
        const pct = cohortTotal > 0 ? (stage.count / cohortTotal) * 100 : 0;
        csvSections.push(
          `${escapeCSVValue(statusLabel(stage.stage))},${formatIntForCSV(stage.count)},${pct.toFixed(1)}%`
        );
      });
      csvSections.push('');

      // Section 5: Organization Breakdown
      csvSections.push('ORGANIZATION BREAKDOWN');
      if (organizationBreakdown.length > 0) {
        csvSections.push('Organization,Total Leads,Signed Up,Enrolled,Enrollment Rate');
        organizationBreakdown.forEach((org) => {
          csvSections.push(
            `${escapeCSVValue(org.sourceOrg || 'Unknown')},${formatIntForCSV(org.totalLeads)},${formatIntForCSV(org.signedUpCount)},${formatIntForCSV(org.enrolledCount)},${(org.enrollmentRate * 100).toFixed(1)}%`
          );
        });
      } else {
        csvSections.push('No organization breakdown available');
      }

      const csv = csvSections.join('\n');
      const filename = `leads-analytics-${getFilesystemSafeTimestamp()}.csv`;
      downloadCSV(csv, filename);
    } catch (error) {
      logger.error('Failed to export analytics', { error: error instanceof Error ? error.message : String(error) });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Card sx={{ minHeight: 'auto', boxShadow: 'none', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>

          {/* Compact KPI Grid - All Leads on left, metrics on top right, Enrolled on bottom right */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 1,
              mb: 0.75,
            }}
          >
            {/* Left: Tall "All matching leads" box */}
            <Tooltip
              title={`This count matches the leads table filters exactly (dataset-scoped, includes status). Cohort-scoped metrics below ignore only the status filter (cohort total: ${formatInt(
                cohortTotal,
              )}).`}
            >
              <Box
                sx={{
                  gridRow: '1 / 3',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 140,
                  p: 2,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  bgcolor: 'primary.50',
                  cursor: 'help',
                }}
              >
                <GroupsIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5, opacity: 0.7 }} />
                <Typography variant="h4" color="primary.main" fontWeight="bold" sx={{ lineHeight: 1, mb: 0.5 }}>
                  {formatInt(datasetTotal)}
                </Typography>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  All matching leads
                </Typography>
              </Box>
            </Tooltip>

            {/* Right side: Two rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Top row: Smaller metric boxes */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 0.75,
                    px: 1,
                    border: '1px solid',
                    borderColor: 'success.light',
                    borderRadius: 1,
                    bgcolor: 'success.50',
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 18, color: 'success.dark', mb: 0.25, opacity: 0.7 }} />
                  <Typography variant="h6" color="success.dark" fontWeight="bold" sx={{ lineHeight: 1 }}>
                    {(cohortSummary.enrollmentRate * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enrollment rate
                  </Typography>
                </Box>

                {timing.avgDaysToEnrollment !== null && timing.avgDaysToEnrollment !== undefined && (
                  <Tooltip
                    title={`For the ${formatInt(
                      enrollmentTimingSampleSize,
                    )} leads with an enrollment date, the average time from upload to enrollment is ${formatAvgDaysExact(
                      timing.avgDaysToEnrollment,
                    )}. This does not include the other ${formatInt(otherEnrollmentTimingLeads)} leads.`}
                  >
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 0.75,
                        px: 1,
                        border: '1px solid',
                        borderColor: 'success.light',
                        borderRadius: 1,
                        bgcolor: 'success.50',
                        cursor: 'help',
                      }}
                    >
                      <ScheduleIcon sx={{ fontSize: 18, color: 'success.dark', mb: 0.25, opacity: 0.7 }} />
                      <Typography variant="h6" color="success.dark" fontWeight="bold" sx={{ lineHeight: 1 }}>
                        {formatAvgDays(timing.avgDaysToEnrollment)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avg time (enrollment)
                      </Typography>
                    </Box>
                  </Tooltip>
                )}

                <Box
                  sx={{
                    textAlign: 'center',
                    py: 0.75,
                    px: 1,
                    border: '1px solid',
                    borderColor: 'info.main',
                    borderRadius: 1,
                    bgcolor: 'info.50',
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 18, color: 'info.main', mb: 0.25, opacity: 0.7 }} />
                  <Typography variant="h6" color="info.main" fontWeight="bold" sx={{ lineHeight: 1 }}>
                    {formatInt(cohortSummary.signedUpCount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Signed up ({formatPct(cohortTotal > 0 ? cohortSummary.signedUpCount / cohortTotal : 0)})
                  </Typography>
                </Box>

                <Tooltip
                  title="Archived after 1 year without enrolling, after enrollment, or by admin action."
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 0.75,
                      px: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      cursor: 'help',
                    }}
                  >
                    <ArchiveIcon sx={{ fontSize: 18, color: 'text.secondary', mb: 0.25, opacity: 0.6 }} />
                    <Typography variant="h6" color="text.primary" fontWeight="bold" sx={{ lineHeight: 1 }}>
                      {formatInt(cohortSummary?.archivedCount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Archived ({formatPct(cohortTotal > 0 ? (cohortSummary?.archivedCount ?? 0) / cohortTotal : 0)})
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>

              {/* Bottom row: Enrolled achievement box spanning full width */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 1,
                  bgcolor: 'success.50',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mr: 1, opacity: 0.8 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" fontWeight="bold" sx={{ lineHeight: 1, mb: 0.25 }}>
                    {formatInt(cohortSummary.enrolledCount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    Enrolled ({formatPct(cohortTotal > 0 ? cohortSummary.enrolledCount / cohortTotal : 0)})
                  </Typography>
                </Box>
              </Box>
            </Box>
        </Box>

        {/* Quick Date Filters */}
        <QuickDateFiltersPanel />

        {/* Details (Accordion + Tabs) */}
        <Accordion
            disableGutters
            elevation={0}
            expanded={detailsExpanded}
            onChange={(_, expanded) => setDetailsExpanded(expanded)}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': { minHeight: 36, px: 0 },
              '& .MuiAccordionSummary-content': { my: 0.5 },
              '& .MuiAccordionDetails-root': { px: 0, pt: 0 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="Export analytics data to CSV">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport();
                      }}
                      disabled={!analytics || exporting}
                      size="small"
                      sx={{ color: 'primary.main' }}
                    >
                      {exporting ? <CircularProgress size={16} /> : <FileDownloadIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <BarChartIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="caption" fontWeight="bold" color="primary.main">
                    Breakdowns
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Tabs
                value={detailsTab}
                onChange={(_, next) => setDetailsTab(next)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 34,
                  px: 0,
                  '& .MuiTab-root': {
                    minHeight: 34,
                    py: 0,
                    px: 1,
                    fontSize: 12,
                    textTransform: 'none',
                  },
                }}
              >
                <Tab value="status" label="Status" />
                <Tab value="journey" label="Journey" />
                <Tab value="orgs" label="By org" />
              </Tabs>
              <Divider />

              <Box sx={{ p: 1, pt: 0.75 }}>
                {detailsTab === 'status' && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Reactivated means the lead was archived at least once. The lead detail shows how many times.
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: 1,
                      }}
                    >
                      {statusPercentages.map((status) => {
                        const tone = statusTone(status.status);
                        return (
                          <Box
                            key={status.status}
                            sx={{
                              p: 1,
                              border: '1px solid',
                              borderRadius: 1,
                              borderColor: tone.borderColor,
                              bgcolor: tone.bgcolor,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                {statusLabel(status.status)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {status.percentage.toFixed(1)}%
                              </Typography>
                            </Box>

                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
                            >
                              {formatInt(status.total)}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              {formatInt(status.unarchived)} active • {formatInt(status.archived)} archived
                            </Typography>

                            <LinearProgress
                              variant="determinate"
                              value={status.percentage}
                              sx={{
                                mt: 0.75,
                                height: 6,
                                borderRadius: 1,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: tone.color,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {detailsTab === 'journey' && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: 1,
                      alignItems: 'stretch',
                    }}
                  >
                    {funnel.stages.map((stage) => {
                      const tone = statusTone(stage.stage);
                      const pct = cohortTotal > 0 ? stage.count / cohortTotal : 0;
                      return (
                        <Box
                          key={stage.stage}
                          sx={{
                            p: 1,
                            border: '1px solid',
                            borderRadius: 1,
                            borderColor: tone.borderColor,
                            bgcolor: tone.bgcolor,
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            {statusLabel(stage.stage)}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
                          >
                            {formatInt(stage.count)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatPct(pct)} of all matching
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={pct * 100}
                            sx={{
                              mt: 0.75,
                              height: 6,
                              borderRadius: 1,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': { bgcolor: tone.color },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {detailsTab === 'orgs' && (
                  <Box>
                    {organizationBreakdown.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No source organization breakdown for the current filters.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ py: 0.5 }}>
                                <strong>Organization</strong>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>
                                <strong>Leads</strong>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>
                                <strong>Signed up</strong>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>
                                <strong>Enrolled</strong>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>
                                <strong>Enrollment %</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {organizationBreakdown.map((org) => (
                              <TableRow key={org.sourceOrgId || 'unknown'} hover>
                                <TableCell sx={{ py: 0.5 }}>{org.sourceOrg || 'Unknown'}</TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                  {formatInt(org.totalLeads)}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                  {formatInt(org.signedUpCount)}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                  {formatInt(org.enrolledCount)}
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                  {(org.enrollmentRate * 100).toFixed(1)}%
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeadsAnalyticsPanel;
